import { FinancialInputs, Property } from '../types';

const RESIDENTIAL_RENTAL_LIFE_YEARS = 27.5;
const DEFAULT_LAND_ALLOCATION_PERCENT = 20;

export type DepreciationEstimate = {
  annualDepreciation: number | null;
  depreciableBasis: number | null;
  buildingBasis: number | null;
  landValue: number | null;
  capitalImprovements: number;
  monthsInService: number;
  methodLabel: string;
  assumptionLabel: string;
  needsEstimate: boolean;
};

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

const getMonthsInService = (placedInServiceDate: string, taxYear: number) => {
  if (!placedInServiceDate) return 12;
  const parsed = new Date(`${placedInServiceDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return 12;

  const serviceYear = parsed.getFullYear();
  if (serviceYear > taxYear) return 0;
  if (serviceYear < taxYear) return 12;
  return Math.max(0, 12 - parsed.getMonth());
};

const resolveAllocationPercents = (inputs: FinancialInputs) => {
  const rawLand = Number.isFinite(inputs.landAllocationPercent) ? inputs.landAllocationPercent : DEFAULT_LAND_ALLOCATION_PERCENT;
  const rawBuilding = Number.isFinite(inputs.buildingAllocationPercent) ? inputs.buildingAllocationPercent : 100 - rawLand;
  const combined = rawLand + rawBuilding;

  if (combined <= 0) {
    return {
      landAllocationPercent: DEFAULT_LAND_ALLOCATION_PERCENT,
      buildingAllocationPercent: 100 - DEFAULT_LAND_ALLOCATION_PERCENT
    };
  }

  const normalizedLand = clampPercent((rawLand / combined) * 100);
  return {
    landAllocationPercent: normalizedLand,
    buildingAllocationPercent: 100 - normalizedLand
  };
};

export const calculateEstimatedDepreciationFromInputs = (
  inputs: FinancialInputs,
  taxYear: number
): DepreciationEstimate => {
  const purchasePrice = Number(inputs.purchasePrice || 0);
  const capitalImprovements = Math.max(0, Number(inputs.capitalImprovements || 0));
  const { landAllocationPercent, buildingAllocationPercent } = resolveAllocationPercents(inputs);
  const monthsInService = getMonthsInService(inputs.placedInServiceDate || '', taxYear);

  if (purchasePrice <= 0) {
    return {
      annualDepreciation: null,
      depreciableBasis: null,
      buildingBasis: null,
      landValue: null,
      capitalImprovements,
      monthsInService,
      methodLabel: 'Residential Rental 27.5 Year SL',
      assumptionLabel: 'Estimate needed',
      needsEstimate: true
    };
  }

  const landValue = purchasePrice * (landAllocationPercent / 100);
  const buildingBasis = purchasePrice * (buildingAllocationPercent / 100);
  const depreciableBasis = buildingBasis + capitalImprovements;
  const fullYearDepreciation = depreciableBasis / RESIDENTIAL_RENTAL_LIFE_YEARS;
  const annualDepreciation = fullYearDepreciation * (monthsInService / 12);

  return {
    annualDepreciation,
    depreciableBasis,
    buildingBasis,
    landValue,
    capitalImprovements,
    monthsInService,
    methodLabel: 'Residential Rental 27.5 Year SL',
    assumptionLabel: inputs.purchasePrice > 0 && !inputs.placedInServiceDate
      ? 'Using purchase price with default land allocation'
      : `Building ${buildingAllocationPercent.toFixed(0)}% / Land ${landAllocationPercent.toFixed(0)}%`,
    needsEstimate: false
  };
};

export const calculateEstimatedDepreciation = (
  property: Property,
  taxYear: number
): DepreciationEstimate => calculateEstimatedDepreciationFromInputs(property.currentYearInputs || property.inputs, taxYear);
